import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import { user } from 'src/StateObserver';
import { FeatureButton } from 'src/game/components/FeatureButton';
import {
  getSuperLollipopWinStreak,
  getSuperLollipopWinStreakRequired,
  isSuperLollipopAvailable,
} from 'src/replicant/superLollipop/superLollipop.getters';
import { AssetGroup } from '@play-co/timestep-core';
import { spineLoader } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { waitForItPromise } from 'src/lib/utils';
import gsap from 'gsap';
import sounds from 'src/lib/sounds';
import { clamp } from 'src/replicant/utils';
import { ProgressBar } from 'src/game/components/shared/ProgressBar';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';

const assets = {
  progressBase: 'assets/ui/superLollipop/button/progress_base.png',
  progressFill: 'assets/ui/superLollipop/button/progress_fill.png',
  spine: {
    loadMethod: spineLoader,
    url: 'assets/ui/superLollipop/animation/home-button',
  },
};

export class SuperLollipopButton extends FeatureButton {
  private streak?: number;
  private progressBar: ProgressBar;

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 144,
      height: 160,
      pressedOffsetY: 5,
      labelPanel: {
        font: 'Title',
        offsetX: 2,
        offsetY: 1,
      },
      ...opts,
    });

    this.animationStateMap = {
      main: {
        type: 'spine',
        assetGroup: new AssetGroup(Object.values(assets)),
        url: assets.spine.url,
        scale: 0.7,
        offsetX: 1,
        offsetY: 80,
        sequence: [this.getIdleAnimation()],
      },
    };

    this.progressBar = new ProgressBar({
      superview: this.container,
      bgStyle: {
        image: assets.progressBase,
        width: downscale(195),
        height: downscale(64),
        centerOnOrigin: true,
        x: this.style.width * 0.5,
        y: this.style.height * 0.5 + 65,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 60, right: 60 },
          vertical: { top: 0, bottom: 0 },
        },
        imgScale: DOWNSCALE_FACTOR,
      },
      fillStyle: {
        image: '', // This fill doesn't have an image, it will just clip its inner image
        width: downscale(171),
        height: downscale(42),
        scaleMethod: 'stretch',
        clip: true,
        x: 8,
        y: 3,
        paddingRight: 14,
      },
      domain: [0, getSuperLollipopWinStreakRequired(user())],
      progress: 0,
      minWidth: 0,
    });

    // Image that goes inside the progress bar view, to be clipped by the fill
    new ImageScaleView({
      superview: this.progressBar.progressFill,
      image: assets.progressFill,
      width: this.progressBar.progressFill.style.width,
      height: this.progressBar.progressFill.style.height,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 60, right: 60 },
        vertical: { top: 0, bottom: 0 },
      },
      imgScale: DOWNSCALE_FACTOR,
    });
  }

  public update() {
    // Hide the button if the feature is not available for the user
    if (!isSuperLollipopAvailable(user())) {
      this.hide();
      return;
    }

    // Instant update the streak if it's undefined or higher than the actual streak
    const actualStreak = getSuperLollipopWinStreak(user());
    if (this.streak === undefined || this.streak > actualStreak) {
      this.streak = actualStreak;
      this.setProgress(this.streak);
    }

    this.show();
    this.setAnimationState('main');

    // Update the win streak label
    const total = getSuperLollipopWinStreakRequired(user());
    const current = Math.min(this.streak, total);
    this.setLabelText(`${current}/${total}`);
  }

  public instantUpdate() {
    this.streak = getSuperLollipopWinStreak(user());
    this.update();
    this.spine?.state.setAnimation(0, this.getIdleAnimation(), true);
    this.setProgress(this.streak);
  }

  public async playProgressAnimation() {
    if (!this.spine) return;
    const newStreak = getSuperLollipopWinStreak(user());
    if (newStreak === this.streak) return;

    // Delayed update streak, to match the animation
    waitForItPromise(800).then(async () => {
      sounds.playSound('progressAwarded', 0.5);
      this.streak = getSuperLollipopWinStreak(user());
      this.update();
      this.setProgressAnimated(newStreak);
      gsap.killTweensOf(this.container.style);
      await gsap.to(this.container.style, {
        scale: 1.1,
        duration: 0.1,
        ease: 'linear',
      });
      await gsap.to(this.container.style, {
        scale: 1,
        duration: 0.3,
        ease: 'back.out',
      });
    });

    // Play spine level up animation in one go, then go back to idle
    sounds.playSound('win', 0.5);
    playSpineAnimationSequence(this.spine, {
      sequence: [this.getLevelUpAnimation(), this.getIdleAnimation()],
      loopLast: true,
    });
  }

  private getIdleAnimation() {
    if (
      this.streak &&
      this.streak >= getSuperLollipopWinStreakRequired(user())
    ) {
      return 'idle';
    }
    return 'idle_noclouds';
  }

  private getLevelUpAnimation() {
    if (
      this.streak &&
      this.streak >= getSuperLollipopWinStreakRequired(user())
    ) {
      return 'lvl_up';
    }
    return 'lvl_up_noclouds';
  }

  private setProgress(value: number) {
    value = Math.round(value);
    value = clamp(value, 0, getSuperLollipopWinStreakRequired(user()));
    this.setLabelText(`${value}/${getSuperLollipopWinStreakRequired(user())}`);
    this.progressBar.progress = value;
  }

  private setProgressAnimated(value: number) {
    value = Math.round(value);
    value = clamp(value, 0, getSuperLollipopWinStreakRequired(user()));
    this.progressBar.animateProgress(value, 300);
  }
}
